import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, doc, updateDoc, setDoc, arrayUnion } from 'firebase/firestore';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { StyledTableCell, StyledTableRow } from '../../utils/styled'
import MuiAlert from '@mui/material/Alert';
import { getCuadrillasOnce } from "../../services/cuadrillasService";
import { getHerramientasOnce } from "../../services/herramientasOnceService";
import { Box, Typography } from "@mui/material";

const firestore = getFirestore();
const currentUser = JSON.parse(localStorage.getItem("currentUser"));
const isAdmin = currentUser?.rol === 'admin';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function StockHerramientas() {
    const [herramientas, setHerramientas] = useState([]);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [quantityHerramientas, setQuantityHerramientas] = useState([]);
    const [initialLoad, setInitialLoad] = useState(true);

    // Edit modal state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedHerramienta, setSelectedHerramienta] = useState(null);
    const [selectedCuadrilla, setSelectedCuadrilla] = useState(null);
    const [editQuantity, setEditQuantity] = useState('');
    const [isEditMode, setIsEditMode] = useState(false); // true if stock doc exists

    const [snackbar, setOpenSnackbar] = useState({ open: false, message: '', type: 'info' });

    const handleCloseSnackbar = () => {
        setOpenSnackbar(prev => ({ ...prev, open: false }));
    };

    const fetchStockData = async () => {
        try {
            const snap = await getDocs(collection(firestore, 'herramientasxcuadrilla'));
            setQuantityHerramientas(snap.docs.map(doc => ({ ...doc.data(), docId: doc.id })));
        } catch (error) {
            console.error('Error al obtener stock de herramientas:', error);
        }
    };

    useEffect(() => {
        const loadAll = async () => {
            try {
                await Promise.all([
                    fetchStockData(),
                    getHerramientasOnce(setHerramientas),
                    getCuadrillasOnce(setCuadrillas)
                ]);
            } catch (err) {
                console.error("Error al cargar datos de stock de herramientas:", err);
            } finally {
                setInitialLoad(false);
            }
        };
        loadAll();
    }, []);

    const handleOpenEdit = (herramienta, cuadrillaItem, currentQty, exists) => {
        setSelectedHerramienta(herramienta);
        setSelectedCuadrilla(cuadrillaItem);
        setEditQuantity(currentQty === '-' ? '0' : String(currentQty));
        setIsEditMode(exists);
        setEditDialogOpen(true);
    };

    const handleSaveStock = async () => {
        if (!selectedHerramienta || !selectedCuadrilla) return;
        const newQty = parseInt(editQuantity, 10);
        if (isNaN(newQty) || newQty < 0) {
            setOpenSnackbar({ open: true, message: 'Ingrese una cantidad válida y positiva', type: 'warning' });
            return;
        }

        try {
            const docId = `${selectedCuadrilla.id}c${selectedHerramienta.id}`;
            const docRef = doc(firestore, 'herramientasxcuadrilla', docId);

            // Find current stock
            const existing = quantityHerramientas.find(
                item => parseInt(item.idCuadrilla, 10) === parseInt(selectedCuadrilla.id, 10) &&
                        parseInt(item.idHerramienta, 10) === parseInt(selectedHerramienta.id, 10)
            );

            const oldQty = existing ? parseInt(existing.cantidad, 10) || 0 : 0;
            const registroStock = {
                fecha: new Date().toISOString(),
                cantidad: newQty,
                cantidadAnterior: oldQty,
                diferencia: newQty - oldQty,
                usuario: currentUser?.email || currentUser?.user || currentUser?.usuario || '-',
                tipo: isEditMode ? 'edicion' : 'creacion'
            };

            if (isEditMode) {
                await updateDoc(docRef, {
                    cantidad: newQty,
                    historialStock: arrayUnion(registroStock)
                });
                setOpenSnackbar({ open: true, message: 'Cantidad editada correctamente', type: 'success' });
            } else {
                await setDoc(docRef, {
                    cantidad: newQty,
                    idCuadrilla: selectedCuadrilla.id,
                    idHerramienta: selectedHerramienta.id,
                    historialStock: [registroStock]
                });
                setOpenSnackbar({ open: true, message: 'Stock guardado correctamente', type: 'success' });
            }

            setEditDialogOpen(false);
            await fetchStockData();
        } catch (error) {
            console.error('Error al guardar el stock:', error);
            setOpenSnackbar({ open: true, message: 'Error al actualizar el stock', type: 'error' });
        }
    };

    if (initialLoad) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Container>
        );
    }

    // Filter visible tools and active crews
    const visibleHerramientas = herramientas.filter(h => h.visible !== false);
    
    // Mostrar solo el stock de cba construir (id 0)
    const cbaCuadrillas = cuadrillas.filter(c => 
        parseInt(c.id, 10) === 0 || 
        String(c.id) === '0' ||
        String(c.numero) === '0' ||
        (c.usuario && c.usuario.toLowerCase().includes('cba')) ||
        (c.numero && c.numero.toLowerCase().includes('cba'))
    );
    const sortedCuadrillas = cbaCuadrillas.length > 0 
        ? cbaCuadrillas 
        : cuadrillas.filter(c => parseInt(c.id, 10) === 1 || String(c.id) === '1');

    return (
        <Container component="main" maxWidth="xl">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Stock de Herramientas por Cuadrilla</Typography>
                <Button variant="contained" href="/herramientas-remito">Nuevo Remito</Button>
            </Box>

            <TableContainer sx={{ maxHeight: '75vh', border: '1px solid #ccc', borderRadius: '4px' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell sx={{ position: 'sticky', left: 0, zIndex: 10, background: 'black' }}>
                                Herramienta
                            </StyledTableCell>
                            {sortedCuadrillas.map(c => (
                                <StyledTableCell key={c.id} align="center" sx={{ minWidth: 120 }}>
                                    {c.usuario ? `${c.usuario} - ${c.numero}` : c.numero}
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleHerramientas.map(herramienta => (
                            <StyledTableRow key={herramienta.id}>
                                <StyledTableCell sx={{ position: 'sticky', left: 0, background: '#f5f5f5', zIndex: 5, fontWeight: 'bold' }}>
                                    {herramienta.name || herramienta.nombre}
                                </StyledTableCell>
                                {sortedCuadrillas.map(cuadrillaItem => {
                                    const stockDoc = quantityHerramientas.find(
                                        item => parseInt(item.idCuadrilla, 10) === parseInt(cuadrillaItem.id, 10) &&
                                                parseInt(item.idHerramienta, 10) === parseInt(herramienta.id, 10)
                                    );
                                    const currentQty = stockDoc ? stockDoc.cantidad : '-';
                                    const exists = !!stockDoc;

                                    return (
                                        <TableCell key={cuadrillaItem.id} align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                <span>{currentQty}</span>
                                                {isAdmin && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEdit(herramienta, cuadrillaItem, currentQty, exists)}
                                                    >
                                                        <EditIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit Stock Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
                <DialogTitle>Editar Stock Manual</DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2">
                            <strong>Herramienta:</strong> {selectedHerramienta?.name}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Cuadrilla:</strong> {selectedCuadrilla?.usuario ? `${selectedCuadrilla.usuario} - ${selectedCuadrilla.numero}` : selectedCuadrilla?.numero}
                        </Typography>
                        <TextField
                            label="Cantidad en Stock"
                            type="number"
                            value={editQuantity}
                            onChange={(e) => setEditQuantity(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveStock} variant="contained">Guardar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.type} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default StockHerramientas;
